// Phase 5B — Display-only badge for parts captured via Parts Assist.
// Renders nothing if the part has no vendor source (manually-entered parts).
// ZERO authority change: pure visual indicator pulled from existing source_* columns.
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Globe } from 'lucide-react';

interface Props {
  /** Vendor code stored in parts_lines.source_vendor (e.g. "oreilly_firstcall"). */
  sourceVendor?: string | null;
  /** Human label captured at intake time. */
  sourceLabel?: string | null;
  /** ISO timestamp from parts_lines.source_captured_at. */
  capturedAt?: string | null;
}

const VENDOR_LABELS: Record<string, string> = {
  oreilly_firstcall: "O'Reilly",
  napa_prolink: 'NAPA',
  usautoforce: 'US AutoForce',
  autozone_pro: 'AutoZone Pro',
  worldpac: 'Worldpac',
  tirerack_wholesale: 'Tire Rack',
  generic: 'Web',
  manual: 'Manual',
};

export function VendorSourceBadge({ sourceVendor, sourceLabel, capturedAt }: Props) {
  if (!sourceVendor) return null;
  const label = sourceLabel || VENDOR_LABELS[sourceVendor] || sourceVendor;
  const captured = capturedAt ? new Date(capturedAt).toLocaleString() : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="text-[9px] py-0 h-3.5 gap-0.5 border-primary/30 text-primary/80"
          >
            <Globe className="h-2.5 w-2.5" />
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-medium">Captured via Parts Assist</div>
          <div className="text-muted-foreground">{label}</div>
          {captured && <div className="text-muted-foreground">{captured}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
